import { useEffect, useState } from "react";
import { ArrowRight, Info, ShieldCheck } from "lucide-react";
import { contactFirstCopy } from "./helpdeskContactFirstCopy";
import "./styles/helpdesk-contact-first.css";

import { HelpdeskContactIcon } from "./HelpdeskContactIcon";
import { useHelpdeskPublicCopy } from "./helpdeskPublicCatalog";

type Profile = {
  version: number;
  signal_label: string;
  signal_url: string;
  whatsapp_label: string;
  whatsapp_url: string;
  telegram_label: string;
  telegram_url: string;
  email: string;
  pgp_fingerprint: string;
  pgp_public_key_sha256: string;
  pgp_public_key_url: string;
  pgp_created_at: string;
  pgp_expires_at: string | null;
  pgp_key_state: "valid" | "expiring" | "expired" | "no_expiry";
  expiry_warning_days: number;
  updated_at: string;
  pgp_public_key_armor: null;
  logo_url: string | null;
};

type LoadState =
  | { state: "loading" }
  | { state: "ready"; profile: Profile }
  | { state: "unavailable" }
  | { state: "failed" };

function mailto(value: string): string {
  return `mailto:${encodeURIComponent(value).replace("%40", "@")}`;
}

export function HelpdeskPublicPage() {
  const { t, locale } = useHelpdeskPublicCopy();
  const copy = contactFirstCopy(locale, t);
  const [load, setLoad] = useState<LoadState>({ state: "loading" });
  const [logoAttempt, setLogoAttempt] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/v1/public/helpdesk", {
      signal: controller.signal,
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (response.status === 404) {
          setLoad({ state: "unavailable" });
          return;
        }
        if (!response.ok) throw new Error("helpdesk profile unavailable");
        setLoad({ state: "ready", profile: await response.json() as Profile });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoad({ state: "failed" });
      });
    return () => controller.abort();
  }, []);

  const profile = load.state === "ready" ? load.profile : null;
  const logoSource = load.state === "loading" ? null : logoAttempt === 0
    ? profile?.logo_url ?? "/api/v1/public/helpdesk/logo"
    : logoAttempt === 1
      ? "/api/v1/public/helpdesk/logo"
      : null;


  const channels = profile ? [
    { channel: "whatsapp" as const, name: "WhatsApp", body: t.whatsappBody, label: profile.whatsapp_label, href: profile.whatsapp_url, action: t.openWhatsapp },
    { channel: "telegram" as const, name: "Telegram", body: t.telegramBody, label: profile.telegram_label, href: profile.telegram_url, action: t.openTelegram },
    { channel: "email" as const, name: t.emailLabel, body: t.emailBody, label: profile.email, href: mailto(profile.email), action: t.writeEmail },
  ] : [];
  return <main className="helpdesk-page helpdesk-contact-first" data-helpdesk-public-page data-public-foundation-control="true">
    <div className="helpdesk-first-shell">
      <section className="helpdesk-first-hero" aria-labelledby="helpdesk-heading">
        <div className={"helpdesk-brand-lockup " + (logoSource ? "has-custom-logo" : "is-fallback")} aria-label={t.logoAria}>
          <span className="helpdesk-brand-logo">{logoSource ? <img src={logoSource} alt="" onError={() => setLogoAttempt((current) => current === 0 && profile?.logo_url ? 1 : 2)} /> : <span className="helpdesk-brand-fallback">M</span>}</span>
          {!logoSource && <span className="helpdesk-brand-copy"><strong>{t.wordmark}</strong><small>{t.byMiaan}</small></span>}
        </div>
        <div><h1 id="helpdesk-heading">{locale === "en" ? copy.title.split(". ").map((line, index) => <span className="helpdesk-first-title-line" key={line}>{index === 0 ? `${line}. ` : line}</span>) : copy.title}</h1><p>{copy.body}</p></div>
        <span className="helpdesk-first-hours">{t.available}</span>
      </section>
      <section className="helpdesk-first-contacts" aria-label={t.contactTitle}>
        {load.state === "loading" && <p className="helpdesk-first-state" role="status">{t.loading}</p>}
        {load.state === "unavailable" && <div className="helpdesk-first-state" role="status"><h2>{t.unavailableTitle}</h2><p>{t.unavailableBody}</p></div>}
        {load.state === "failed" && <div className="helpdesk-first-state" role="alert"><h2>{t.loadFailed}</h2><p>{t.unavailableBody}</p></div>}
        {profile && <>
          <div className="helpdesk-first-channel-grid">
            <article className="helpdesk-first-signal">
              <div className="helpdesk-first-signal-top"><HelpdeskContactIcon channel="signal" /><span>{copy.recommended}</span></div>
              <h2>Signal</h2><p>{t.signalBody}</p><p>{t.safety[0].items[0]}</p>
              <a className="helpdesk-first-contact-value" data-helpdesk-contact data-kavoosh-technical href={profile.signal_url} target="_blank" rel="noreferrer">{profile.signal_label}</a>
              <a className="helpdesk-first-primary" href={profile.signal_url} target="_blank" rel="noreferrer">{t.openSignal}</a>
            </article>
            <div className="helpdesk-first-alternatives">{channels.map((item) => <article className="helpdesk-first-channel" key={item.channel}>
              <HelpdeskContactIcon channel={item.channel} /><div><h2>{item.name}</h2><p>{item.body}</p>{item.channel === "email" && <a data-helpdesk-contact data-kavoosh-technical className="helpdesk-first-contact-value" href={item.href} {...(item.channel === "email" ? {} : { target: "_blank", rel: "noreferrer" })}>{item.label}</a>}</div>
              <a className="helpdesk-first-channel-action" href={item.href} {...(item.channel === "email" ? {} : { target: "_blank", rel: "noreferrer" })}>{item.action}</a>
            </article>)}</div>
          </div><p className="helpdesk-first-shared-team">{t.contactBody}</p>
        </>}
      </section>
      <section className="helpdesk-first-message" aria-labelledby="helpdesk-first-message-title">
        <h2 id="helpdesk-first-message-title">{copy.firstTitle}</h2>
        <ol>{copy.prompts.map((item, index) => <li key={index}><span aria-hidden="true">{new Intl.NumberFormat(locale).format(index + 1)}</span><div>{item.title && <h3>{item.title}</h3>}<p>{item.body}</p></div></li>)}</ol>
        <p className="helpdesk-first-warning"><Info size={20} aria-hidden="true" />{copy.warning}</p>
        <details className="helpdesk-first-safety-details"><summary>{t.safety[1].title}</summary><ul>{t.safety[1].items.map((item) => <li key={item}>{item}</li>)}</ul></details>
      </section>
      {profile && <section className="helpdesk-first-pgp" aria-labelledby="helpdesk-first-pgp-title">
        <div className="helpdesk-first-key-heading"><ShieldCheck size={46} aria-hidden="true" /><div><h2 id="helpdesk-first-pgp-title">{copy.pgpTitle}</h2><p>{copy.pgpBody}</p></div></div>
        <a className="helpdesk-first-key-download" href={profile.pgp_public_key_url} download="helpdesk-public-key.asc">{t.downloadKey}</a>
        <details><summary>{copy.fingerprintAction}</summary><code data-helpdesk-contact data-kavoosh-technical>{profile.pgp_fingerprint}</code></details>
        {(profile.pgp_key_state === "expiring" || profile.pgp_key_state === "expired") && <p className="helpdesk-first-key-warning" role="status">{profile.pgp_key_state === "expired" ? t.pgpExpired : t.pgpExpiring}</p>}
      </section>}
      <section className="helpdesk-first-next" aria-labelledby="helpdesk-next-title"><h2 id="helpdesk-next-title">{t.safety[2].title}</h2><ol>{copy.next.map((item, index) => <li key={item}><span>{new Intl.NumberFormat(locale).format(index + 1)}</span><p>{item}</p>{index < 2 && <ArrowRight size={18} aria-hidden="true" />}</li>)}</ol></section>
      <aside className="helpdesk-first-boundary"><Info size={18} aria-hidden="true" /><p><strong>{t.boundaryTitle}</strong> {t.boundaryBody}</p></aside>
    </div>
  </main>;
}
